// Actor-bound parent RPC queue. Only a positive 230 receipt acknowledges work.
import {useCallback, useEffect, useRef, useState} from 'react';

import {sb} from './supabase.js';
import type {SupabaseClient} from './supabaseTypes.js';
import {queuedReportActor} from './queuedReportApi.js';
import {photoDigest} from './queuedReportPhotos.js';
import {submitQueuedRpc} from './queuedRpcApi.js';
import {newClientSubmissionId} from './clientSubmissionId.js';
import {buildRpcRequest, getRpcFormConfig, isRpcRequest} from './offlineRpcForms.js';
import type {RpcFormPayload, RpcRequest} from './offlineRpcForms.js';
import {
  enqueueSubmissionWithPhotos,
  listPhotoBlobsByCsid,
  listQueued,
  listStuck,
  markSyncing,
  acknowledgeQueuedReport,
  getSubmission,
  markFailed,
  markStuckNow,
  recoverStaleSyncing,
  retrySubmission,
  discardSubmission,
} from './offlineQueue.js';
import type {PreparedPhoto, QueuedSubmission} from './offlineQueue.js';
import {compressImage} from './photoCompress.js';
import {
  TASK_REQUEST_PHOTOS_BUCKET,
  TASK_REQUEST_PHOTO_DEFAULT_FILENAME,
  buildTaskRequestPhotoStoragePath,
  buildTaskRequestPhotoDbPath,
  isStorageDuplicateError,
} from './tasks.js';

const TICK_INTERVAL_MS = 60_000;

// ----------------------------------------------------------------------------
// The shapes this hook reasons about
// ----------------------------------------------------------------------------

/**
 * What the classifier actually reads off a failure. Every field optional: this
 * path is reached by rejected fetches (a bare TypeError), by PostgREST
 * `{error}` envelopes, and by storage errors, and not one of those guarantees
 * any particular field — which is the whole reason the classifier exists.
 */
export type RpcSubmitError = {
  name?: string;
  message?: string;
  code?: string | number;
  status?: string | number;
};

/** A plain Error carrying the PostgREST code/status the classifier keys on. */
type WrappedRpcError = Error & {code?: string | number; status?: string | number; cause?: unknown};

/** The `{data, error}` envelope sb.rpc resolves with. */
export type RpcResponse = {data: unknown; error: RpcSubmitError | null};

export type RpcSubmitState = 'synced' | 'queued';

export type RpcSubmitResult = {
  state: RpcSubmitState;
  csid: string;
  parentId: string;
  record: RpcRequest;
  data: unknown;
};

/**
 * Per-call overrides. This is the REAL second parameter of submit() — see the
 * note on useOfflineRpcSubmit's return type.
 */
export type RpcSubmitOptions = {
  /** pin the client_submission_id (offline-replay tests do this) */
  csid?: string;
  /**
   * pin the parent row id. TasksWebform passes a 'ti-…' id so the queued
   * record's parent.id matches the task_instances PK convention.
   */
  parentId?: string;
  /**
   * a single image for a hasPhoto:true form-kind. Ignored by every registry
   * entry without that flag.
   */
  photo?: Blob | null;
  replacesCsid?: string;
  expectedActorProfileId?: string;
  /**
   * The exact File a recovery Open restored from the queue. When opts.photo
   * IS this object, its bytes are already the frozen compressed JPEG and are
   * reused verbatim (no lossy re-encode of the original photo).
   */
  retainedPhoto?: Blob | null;
};

export type OfflineRpcSubmitApi = {
  /**
   * `opts` is OPTIONAL, not absent. The implementation has always taken it
   * (`async (payload, opts = {}) => …`) and TasksWebform has always passed it
   * — `submit(payload, {parentId, photo})` is how the task-request photo and
   * the 'ti-…' id reach the queue at all. The previous JSDoc declared a
   * one-parameter function, which was simply wrong about this module: it made
   * every TypeScript caller of the photo path a TS2554 arity error while the
   * running code was correct.
   */
  submit: (payload: RpcFormPayload, opts?: RpcSubmitOptions) => Promise<RpcSubmitResult>;
  syncNow: () => Promise<void>;
  stuckRows: QueuedSubmission[];
  queuedCount: number;
  refresh: () => Promise<void>;
  retryStuck: (csid: string) => Promise<void>;
  discardStuck: (csid: string) => Promise<void>;
  syncing: boolean;
};

type ErrorClass = 'network' | 'server' | 'rls' | 'schema' | 'unknown';

// A non-null, non-array object. Errors, envelopes and IndexedDB reads all
// arrive as opaque values; this is the one narrowing they go through.
function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// Read a message off an unknown thrown value without asserting a shape.
function errMessage(err: unknown): string {
  return isPlainRecord(err) && typeof err.message === 'string' ? err.message : String(err);
}

function classifyError(err: unknown): ErrorClass {
  if (isPlainRecord(err) && err.code === 'WCF01') return 'schema';
  if (err instanceof TypeError) return 'network';
  const e = isPlainRecord(err) ? err : null;
  if (e && e.name === 'TypeError') return 'network';
  if (e && typeof e.message === 'string' && /failed to fetch|network ?error|load failed/i.test(e.message)) {
    return 'network';
  }

  const status = e && e.status != null ? Number(e.status) : null;
  const code = e && e.code != null ? String(e.code) : '';

  if (status != null) {
    if (status >= 500) return 'server';
    if (status === 401 || status === 403) return 'rls';
    if (status >= 400 && status < 500) {
      // P0001 = PL/pgSQL `RAISE EXCEPTION` default SQLSTATE (raise_exception).
      // Mig 034's submit_add_feed_batch and mig 035's
      // submit_weigh_in_session_batch use bare RAISE EXCEPTION for every
      // validation path (missing csid / id / date / team_member / 0 entries
      // / bad species / bad status / bad broiler_week). PostgREST surfaces
      // those as status=400 + code='P0001'. Without the explicit match
      // they would fall to 'unknown' → enqueue, which would burn retry
      // budget on a deterministic input bug.
      if (/^PGRST/i.test(code) || /^23/.test(code) || /^22/.test(code) || code === 'P0001') return 'schema';
      return 'unknown';
    }
  }

  // Codeless errors with a 23xxx / P0001 code (some PostgREST shapes drop the status).
  if (/^23/.test(code) || /^22/.test(code) || /^PGRST/i.test(code) || code === 'P0001') return 'schema';

  return 'unknown';
}

// ── hasPhoto branch helpers (C3.1b: task_submit only) ──────────────────
//
// Opt-in via cfg.hasPhoto in offlineRpcForms.js. weigh_in_session_batch
// and add_feed_batch don't carry that flag, so their behavior is byte-
// identical to today. Only registered hasPhoto:true entries flow through
// the photo upload + blob persistence + replay-re-upload path below.

const REQUEST_PHOTO_KEY = 'photo-1';

/**
 * Compress an arbitrary File/Blob into the canonical task-request-photo
 * shape the queue + storage flow expects. Deterministic photo_key +
 * path so replay uploads to the same storage object idempotently
 * (upsert:false + duplicate-as-success at the storage level).
 * `retained` = the bytes are a recovered queued photo that was already
 * compressed once; they are kept exactly rather than re-encoded.
 */
async function prepareTaskRequestPhoto(blobOrFile: Blob, parentId: string, retained = false): Promise<PreparedPhoto> {
  if (!blobOrFile) {
    throw new Error('useOfflineRpcSubmit: opts.photo must be a Blob or File');
  }
  if (Array.isArray(blobOrFile)) {
    // One-photo-max contract for v1 — surface the misuse loudly.
    throw new TypeError('useOfflineRpcSubmit: opts.photo must be a single Blob/File, not an array');
  }
  const compressed = retained ? blobOrFile : await compressImage(blobOrFile);
  const filename = TASK_REQUEST_PHOTO_DEFAULT_FILENAME;
  return {
    photo_key: REQUEST_PHOTO_KEY,
    path: buildTaskRequestPhotoStoragePath(parentId, filename),
    blob: compressed,
    mime: compressed.type || 'image/jpeg',
    size_bytes: compressed.size || 0,
    // `name` exists on File, not on Blob, and this accepts either — a
    // File-backed pick keeps its original filename, a bare Blob falls back to
    // the canonical one, exactly as before.
    name: (blobOrFile instanceof File ? blobOrFile.name : '') || filename,
    captured_at: new Date().toISOString(),
  };
}

/**
 * Single-shot upload to the task-request-photos bucket.
 *
 * Retry-safety (Codex C2 review): the task-request-photos bucket is
 * intentionally append-only — mig 042 has no UPDATE policy, and
 * Supabase storage's `upsert:true` would need one. Using upsert:false
 * everywhere and treating Duplicate / 409 / "already exists" as
 * idempotent success. Both submit-time first attempts AND replay
 * re-uploads end up at the same deterministic '<id>/photo-1.jpg'
 * path, so duplicate-as-success covers:
 *   - submit-time retry where a previous attempt's bytes are still
 *     in the bucket (rare; e.g. tab-killed mid-RPC after upload
 *     succeeded),
 *   - replay re-upload after a queued submission's blob landed on
 *     the original attempt.
 *
 * Throws on any non-duplicate storage error so the caller can route
 * to the queued / stuck branch. 230: only the replay drain calls this,
 * after the frozen path and byte digest are proven.
 */
async function uploadTaskRequestPhoto(
  sbClient: SupabaseClient,
  storagePath: string,
  blob: Blob,
  mime?: string,
): Promise<void> {
  const {error} = await sbClient.storage
    .from(TASK_REQUEST_PHOTOS_BUCKET)
    .upload(storagePath, blob, {contentType: mime || 'image/jpeg', upsert: false});
  if (error && !isStorageDuplicateError(error)) throw error;
}

async function requireActor(sbClient: SupabaseClient, actor: string): Promise<void> {
  if ((await queuedReportActor(sbClient)) !== actor) {
    throw Object.assign(new Error('Sign in with the account that queued this report.'), {code: 'WCF01'});
  }
}

// No automatic upload or RPC for legacy identity, another actor, or superseded work.
export async function _syncQueuedRpcEntry(
  formKind: string,
  entry: QueuedSubmission,
  sbClient: SupabaseClient = sb,
): Promise<RpcResponse | null> {
  try {
    if (!entry.actor_profile_id || entry.replacement_csid) {
      throw Object.assign(
        new Error(
          entry.replacement_csid
            ? 'A replacement is pending. Open it to continue.'
            : 'This older report has no verified account. Open it to review.',
        ),
        {code: 'WCF01'},
      );
    }
    await requireActor(sbClient, entry.actor_profile_id);
    const cfg = getRpcFormConfig(formKind);
    const record = entry.record;
    if (
      !isRpcRequest(record) ||
      record.rpc !== cfg.rpc ||
      !isPlainRecord(record.args.parent_in) ||
      record.args.parent_in.client_submission_id !== entry.csid
    ) {
      throw Object.assign(new Error('The stored request needs help. Open it to review.'), {code: 'WCF01'});
    }
    await markSyncing(entry.csid);
    const blobs = await listPhotoBlobsByCsid(entry.csid);
    const parent = record.args.parent_in;
    const path = parent.request_photo_path;
    if (cfg.hasPhoto && path) {
      if (
        !record.photo ||
        record.photo.path !== path ||
        blobs.length !== 1 ||
        path !== `${TASK_REQUEST_PHOTOS_BUCKET}/${blobs[0].key}` ||
        !/^[a-f0-9]{64}$/.test(record.photo.sha256) ||
        (await photoDigest(blobs[0].blob)) !== record.photo.sha256
      ) {
        throw Object.assign(new Error('Queued photo bytes are missing or changed. The report was retained.'), {
          code: 'WCF01',
        });
      }
      await requireActor(sbClient, entry.actor_profile_id);
      await uploadTaskRequestPhoto(sbClient, blobs[0].key, blobs[0].blob, blobs[0].mime);
    } else if (blobs.length || record.photo) {
      throw Object.assign(new Error('Queued photo details need help. The report was retained.'), {code: 'WCF01'});
    }
    await requireActor(sbClient, entry.actor_profile_id);
    const response = await submitQueuedRpc(sbClient, formKind, record, entry.actor_profile_id);
    if (response.error) throw response.error;
    await acknowledgeQueuedReport(entry.csid, entry.actor_profile_id);
    return response;
  } catch (error) {
    const message = errMessage(error);
    const shaped = isPlainRecord(error) ? error : {};
    const code = 'code' in Object(error) ? String((error as {code?: unknown}).code || '') : '';
    if (code === 'WCF01' || classifyError(error) === 'schema') await markStuckNow(entry.csid, message);
    else await markFailed(entry.csid, message);
    return {
      data: null,
      error: {
        message,
        code: code || undefined,
        status: typeof shaped.status === 'number' || typeof shaped.status === 'string' ? shaped.status : undefined,
      },
    };
  }
}

/**
 * formKind — RPC registry key (e.g. 'add_feed_batch').
 */
export function useOfflineRpcSubmit(formKind: string): OfflineRpcSubmitApi {
  const [stuckRows, setStuckRows] = useState<QueuedSubmission[]>([]);
  const [queuedCount, setQueuedCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const mountedRef = useRef(true);
  const inFlightRef = useRef(false);

  // Validate the form_kind once at mount — typos blow up loud, not silent.
  useEffect(() => {
    getRpcFormConfig(formKind);
  }, [formKind]);

  const refresh = useCallback(async () => {
    const [queued, stuck] = await Promise.all([listQueued(formKind), listStuck(formKind)]);
    if (!mountedRef.current) return;
    setQueuedCount(queued.length);
    setStuckRows(stuck);
  }, [formKind]);

  const syncNow = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    if (mountedRef.current) setSyncing(true);
    try {
      await recoverStaleSyncing(formKind);

      const queued = await listQueued(formKind);
      for (const entry of queued) await _syncQueuedRpcEntry(formKind, entry);
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) setSyncing(false);
      await refresh();
    }
  }, [formKind, refresh]);

  const submit = useCallback(
    async (payload: RpcFormPayload, opts: RpcSubmitOptions = {}) => {
      return _runSubmit({formKind, payload, opts, refresh});
    },
    [formKind, refresh],
  );

  const retryStuck = useCallback(
    async (csid: string) => {
      await retrySubmission(csid);
      await refresh();
      await syncNow();
    },
    [refresh, syncNow],
  );

  const discardStuck = useCallback(
    async (csid: string) => {
      await discardSubmission(csid);
      await refresh();
    },
    [refresh],
  );

  // Mount: load existing stuck/queued state, fire one sync pass.
  useEffect(() => {
    mountedRef.current = true;
    refresh();
    syncNow();
    return () => {
      mountedRef.current = false;
    };
  }, [refresh, syncNow]);

  // Background triggers: online event + 60s tick.
  useEffect(() => {
    function handleOnline() {
      syncNow();
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('online', handleOnline);
    }
    const id = setInterval(() => {
      syncNow();
    }, TICK_INTERVAL_MS);
    return () => {
      if (typeof window !== 'undefined') window.removeEventListener('online', handleOnline);
      clearInterval(id);
    };
  }, [syncNow]);

  return {
    submit,
    syncNow,
    stuckRows,
    queuedCount,
    refresh,
    retryStuck,
    discardStuck,
    syncing,
  };
}

// Internal helper exported for tests.
export const _classifyError = classifyError;

// Persist the complete frozen intent before any upload/RPC. Replacement links
// are atomic in IDB; the old row and photos survive until a verified receipt.
export async function _runSubmit({
  formKind,
  payload,
  opts = {},
  refresh,
}: {
  formKind: string;
  payload: RpcFormPayload;
  opts?: RpcSubmitOptions;
  refresh?: (() => Promise<void>) | null;
}): Promise<RpcSubmitResult> {
  const actorProfileId = await queuedReportActor(sb);
  if (!actorProfileId || (opts.expectedActorProfileId && opts.expectedActorProfileId !== actorProfileId)) {
    throw new Error('Sign in with the account that opened this report. Your answers were retained.');
  }
  const csid = opts.csid ?? newClientSubmissionId();
  const parentId = opts.parentId ?? `${formKind}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const cfg = getRpcFormConfig(formKind);
  let photo: PreparedPhoto | null = null;
  if (cfg.hasPhoto && opts.photo) {
    photo = await prepareTaskRequestPhoto(opts.photo, parentId, opts.photo === opts.retainedPhoto);
  }
  const requestPhotoDbPath = photo ? buildTaskRequestPhotoDbPath(parentId, TASK_REQUEST_PHOTO_DEFAULT_FILENAME) : null;
  const record = buildRpcRequest(formKind, payload, {csid, parentId, requestPhotoDbPath});
  if (photo) record.photo = {path: requestPhotoDbPath!, sha256: await photoDigest(photo.blob)};
  await requireActor(sb, actorProfileId);
  await enqueueSubmissionWithPhotos({
    formKind,
    csid,
    actorProfileId,
    replacesCsid: opts.replacesCsid,
    payload,
    record,
    photos: photo ? [photo] : [],
  });
  const entry = await getSubmission(csid);
  if (!entry) throw new Error('Could not preserve the queued report. Your answers are still on this form.');
  const result = await _syncQueuedRpcEntry(formKind, entry);
  if (refresh) await refresh();
  if (result?.error && (result.error.code === 'WCF01' || classifyError(result.error) === 'schema')) {
    const error: WrappedRpcError = new Error(
      `Could not save: ${result.error.message}. Open the retained report in Needs Help.`,
    );
    error.code = result.error.code;
    error.status = result.error.status;
    throw error;
  }
  return {state: result && !result.error ? 'synced' : 'queued', csid, parentId, record, data: result?.data ?? null};
}
